<script lang="ts">
import type { ModelModifiers, TextareaEmits, TextareaSlots, TextareaProps as UTextareaProps } from '#ui/types';

type TextareaValue = string | number;

export interface TextareaProps<T extends TextareaValue = TextareaValue, Mod extends ModelModifiers = ModelModifiers> extends UTextareaProps<T, Mod> {
	/**
	 * Take full width
	 */
	block?: boolean;
	/**
	 * Show copy button
	 */
	copy?: boolean;
	/**
	 * Make the textarea readonly
	 */
	readonly?: boolean;
}
</script>

<script setup lang="ts" generic="T extends TextareaValue = TextareaValue, Mod extends ModelModifiers = ModelModifiers">
import Textarea from '@nuxt/ui/components/Textarea.vue';
import { reactiveOmit } from '@vueuse/core';
import { useForwardExpose, useForwardPropsEmits } from 'reka-ui';
import { computed } from 'vue';
import { isNil } from '#layers/base/public';
import { mergeUi } from '#layers/ui/utils/merge-ui';

const props = defineProps<TextareaProps<T, Mod>>();
const emit = defineEmits<TextareaEmits<T, Mod>>();
const slots = defineSlots<TextareaSlots>();

const forward = useForwardPropsEmits(reactiveOmit(props, 'copy'), emit);
const { forwardRef } = useForwardExpose();

const ui = computed(() => mergeUi(props.ui, {
	trailing: props.copy ? 'flex flex-col items-center w-10 px-0!' : '',
}));

const copyButtonSize = computed(() => props.size === 'lg' || props.size === 'xl' || props.size == null ? 'md' : props.size);
</script>

<template>
	<Textarea
		:ref="forwardRef"
		v-bind="forward"
		:class="[props.class, props.block ? 'w-full' : '']"
		:ui
	>
		<template v-for="(_, name) in slots" #[name]="slotData: any">
			<slot :name v-bind="slotData" />
		</template>

		<template v-if="copy && !isNil(props.modelValue)" #trailing>
			<DCopyButton :value="String(modelValue)" :size="copyButtonSize" />
		</template>
	</Textarea>
</template>
